<script setup lang="ts">
import { useFormSubmit } from '@hatfieldmedia/hmcore'
import { computed, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'

const props = defineProps<{
  modelValue: boolean
  preselectedInterest?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const phone = ref('')
const interest = ref('')
const organization = ref('')
const message = ref('')

const { submit, reset, isSubmitting, isComplete, error } = useFormSubmit({
  formName: 'contact',
  data: computed(() => ({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    phone: phone.value,
    interest: interest.value,
    organization: organization.value,
    message: message.value,
  })),
})

watch(
  () => props.preselectedInterest,
  (val) => {
    if (val) interest.value = val
  },
  { immediate: true },
)

watch(
  () => props.modelValue,
  (open) => {
    if (open && props.preselectedInterest) {
      interest.value = props.preselectedInterest
    }
  },
)

function resetForm() {
  firstName.value = ''
  lastName.value = ''
  email.value = ''
  phone.value = ''
  interest.value = ''
  organization.value = ''
  message.value = ''
  reset()
}

function close() {
  emit('update:modelValue', false)
  if (isComplete.value) {
    resetForm()
  }
}

async function handleSubmit() {
  await submit()
}
</script>

<template>
  <BaseModal :model-value="modelValue" max-width="500px" @update:model-value="close">
    <div class="cf-body">
      <!-- Form state -->
      <template v-if="!isComplete">
        <h2>Get in Touch</h2>
        <p class="cf-subtitle">
          Have questions? Want to learn more? Fill out the form and we'll reach out.
        </p>

        <form @submit.prevent="handleSubmit">
          <div class="form-row">
            <div class="form-group">
              <label for="cf-first-name">First Name *</label>
              <input
                id="cf-first-name"
                v-model="firstName"
                type="text"
                placeholder="First name"
                required
              />
            </div>
            <div class="form-group">
              <label for="cf-last-name">Last Name *</label>
              <input
                id="cf-last-name"
                v-model="lastName"
                type="text"
                placeholder="Last name"
                required
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="cf-email">Email *</label>
              <input
                id="cf-email"
                v-model="email"
                type="email"
                placeholder="you@email.com"
                required
              />
            </div>
            <div class="form-group">
              <label for="cf-phone">Phone</label>
              <input id="cf-phone" v-model="phone" type="tel" placeholder="(555) 555-5555" />
            </div>
          </div>

          <div class="form-row single">
            <div class="form-group">
              <label for="cf-interest">I'm Interested In</label>
              <select id="cf-interest" v-model="interest">
                <option value="" disabled>Select a service</option>
                <option value="1on1">1-on-1 Testing Evaluations</option>
                <option value="team">Team Evaluations</option>
                <option value="combine">Combine Events</option>
                <option value="other">Other / General Inquiry</option>
              </select>
            </div>
          </div>

          <div class="form-row single">
            <div class="form-group">
              <label for="cf-org">Organization / Team Name</label>
              <input
                id="cf-org"
                v-model="organization"
                type="text"
                placeholder="School, team, or organization"
              />
            </div>
          </div>

          <div class="form-row single">
            <div class="form-group">
              <label for="cf-message">Message</label>
              <textarea
                id="cf-message"
                v-model="message"
                rows="4"
                placeholder="Tell us what you're looking for..."
              ></textarea>
            </div>
          </div>

          <p v-if="error" class="form-error">Something went wrong. Please try again.</p>

          <button type="submit" class="modal-submit" :disabled="isSubmitting">
            {{ isSubmitting ? 'Sending...' : 'Send Message' }}
          </button>
          <p class="form-note">We'll get back to you within 24 hours.</p>
        </form>
      </template>

      <!-- Success state -->
      <template v-else>
        <div class="success-state">
          <div class="success-icon">✓</div>
          <h3>Message Sent!</h3>
          <p>We'll be in touch soon.</p>
          <button class="modal-submit done-btn" @click="close">Done</button>
        </div>
      </template>
    </div>
  </BaseModal>
</template>

<style scoped>
.cf-body {
  padding: 32px;
}

h2 {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 950;
  font-size: 1.5rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 4px;
  text-align: center;
}

.cf-subtitle {
  font-family: 'HND', sans-serif;
  font-size: 0.88rem;
  color: var(--text);
  text-align: center;
  margin-bottom: 24px;
}

.form-row {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.form-row.single {
  flex-direction: column;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-family: 'HND', sans-serif;
  font-size: 0.72rem;
  color: var(--pink-bright);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
  font-weight: 600;
}

.form-group input,
.form-group select,
.form-group textarea {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px 14px;
  color: var(--white);
  font-family: 'HND', sans-serif;
  font-size: 0.9rem;
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: var(--pink);
  box-shadow: 0 0 0 3px rgba(208, 23, 122, 0.12);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.form-group select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23C8D0DC' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
}

.form-group select option {
  background: var(--card);
  color: var(--white);
}

.form-group textarea {
  resize: vertical;
}

.modal-submit {
  width: 100%;
  padding: 16px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--pink), var(--purple));
  color: #fff;
  font-family: 'HND', sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-top: 8px;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.modal-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(208, 23, 122, 0.4);
}

.modal-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-note {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  color: rgba(200, 208, 220, 0.5);
  text-align: center;
  margin-top: 12px;
}

.form-error {
  font-family: 'HND', sans-serif;
  font-size: 0.85rem;
  color: var(--color-error);
  text-align: center;
  margin-bottom: 8px;
}

/* Success state */
.success-state {
  text-align: center;
  padding: 32px 0;
}

.success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--pink), var(--purple));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 1.5rem;
  color: #fff;
}

.success-state h3 {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 950;
  font-size: 1.3rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 6px;
}

.success-state p {
  font-family: 'HND', sans-serif;
  font-size: 0.9rem;
  color: var(--text);
}

.done-btn {
  max-width: 200px;
  margin: 20px auto 0;
}

/* Mobile */
@media (max-width: 768px) {
  .cf-body {
    padding: 24px 20px;
  }

  .cf-body h2 {
    font-size: 1.4rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .form-group {
    margin-bottom: 14px;
  }
}
</style>
